import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import filter from "lodash.filter";
import isEmpty from "lodash.isempty";
import keyBy from "lodash.keyby";

import { WorkoutSource } from "../../entities/ClientWorkout";
import { type RejectedTrainingFeedbackData, type TrainingFeedbackData } from "../../entities/Measurement";
import { ActivityEventType, type WorkoutActivityEventWithFeedback } from "../../entities/ProgramActivity";
import {
  TrainingStatus,
  type ClientProgramStatus,
  type ClientTrainingProgram,
  type ClientTrainingProgramDetails,
  type ProgramSchedule,
  type ProgramWorkout,
} from "../../entities/TrainingProgram";

dayjs.extend(isoWeek);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export type ProgramWorkoutWithMetadata = ProgramWorkout & {
  status: TrainingStatus | null;
  source: WorkoutSource;
  activityId?: string;
  workoutRate?: TrainingFeedbackData | RejectedTrainingFeedbackData | null;
  feedbackId?: string | null;
  /**
   * TRE-1826 — trening pochodzi z planu innego niż aktywny (zaplanowanego albo
   * archiwalnego). Dziennik ma go pokazać, ale bez zapisu statusów, ocen, nagrań
   * i bez przestawiania kolejności.
   */
  readOnly?: boolean;
  programId?: string;
  programStatus?: ClientProgramStatus;
};

export type PreviewProgram = {
  program: ClientTrainingProgram;
  details: ClientTrainingProgramDetails | null;
};

export type GetWorkoutsForDayParams = {
  selectedDate: dayjs.Dayjs;
  clientProgram?: ClientTrainingProgram | null;
  activitiesForSelectedDate:
    | Record<string, WorkoutActivityEventWithFeedback>
    | WorkoutActivityEventWithFeedback[]
    | null;
  showOnlyForCurrentProgram?: boolean;
  programDetails: ClientTrainingProgramDetails | null;
  /** TRE-1826 — plany `scheduled` / `archived` pokrywające wybrany dzień. */
  previewPrograms?: PreviewProgram[];
};

export const weekdays = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

export class WorkoutService {
  static findCurrentWeekScheduleIndex(schedule: ProgramSchedule | null, startDate: string, currentDay: dayjs.Dayjs) {
    let currentWeekIndex = 0;
    if (schedule && schedule.length > 1) {
      const weekAIndex = dayjs(startDate).isoWeek() % 2;
      const weekBIndex = dayjs(startDate).add(1, "weeks").isoWeek() % 2;
      const weekMap = {
        0: weekAIndex,
        1: weekBIndex,
      };
      currentWeekIndex = weekMap[(currentDay.isoWeek() % 2) as 0 | 1];
    }
    return currentWeekIndex;
  }

  static getWorkoutStatus(
    workout: ProgramWorkout,
    workoutDate: dayjs.Dayjs,
    fromSchedule?: boolean,
  ): {
    status: TrainingStatus | null;
  } {
    if (!workout) {
      return {
        status: null,
      };
    }
    const isBeforeToday = workoutDate?.isBefore(dayjs().endOf("day"), "day");
    const { exercises, preWorkout, postWorkout } = workout;
    const allExercises = [...preWorkout, ...exercises, ...postWorkout];

    if (!allExercises.length) {
      return {
        status: TrainingStatus.EMPTY,
      };
    }

    if (fromSchedule) {
      if (isBeforeToday) {
        return {
          status: TrainingStatus.PASSED,
        };
      }
      return {
        status: TrainingStatus.NEW,
      };
    }

    const workoutsCount = allExercises.length;

    const fulfilledWorkouts = filter(allExercises, {
      status: TrainingStatus.FULFILLED,
    }).length;
    const partiallyFulfilled = filter(allExercises, {
      status: TrainingStatus.PARTIALLY_FULFILLED,
    }).length;
    const rejectedWorkouts = filter(allExercises, {
      status: TrainingStatus.REJECTED,
    }).length;

    if (workoutsCount === fulfilledWorkouts) {
      return {
        status: TrainingStatus.FULFILLED,
      };
    }
    if (
      (fulfilledWorkouts && workoutsCount > fulfilledWorkouts) ||
      (partiallyFulfilled && workoutsCount > partiallyFulfilled)
    ) {
      return {
        status: TrainingStatus.PARTIALLY_FULFILLED,
      };
    }
    if (!fulfilledWorkouts && isBeforeToday) {
      return {
        status: TrainingStatus.PASSED,
      };
    }
    if (workoutsCount === rejectedWorkouts) {
      return {
        status: TrainingStatus.REJECTED,
      };
    }
    return {
      status: TrainingStatus.NEW,
    };
  }

  /** Treningi zapisane w aktywnościach dla wybranego dnia, niezależnie od planu. */
  static getSavedWorkoutsForDay(
    activitiesForSelectedDate: GetWorkoutsForDayParams["activitiesForSelectedDate"],
    selectedDate: dayjs.Dayjs,
  ): ProgramWorkoutWithMetadata[] {
    if (!activitiesForSelectedDate) {
      return [];
    }

    return Object.values(activitiesForSelectedDate)
      .filter(
        (event) => event.type === ActivityEventType.Workout && event.eventDate === selectedDate.format("YYYY-MM-DD"),
      )
      .map(({ data: workout, id, workoutRate, feedbackId }) => ({
        ...workout,
        status: this.getWorkoutStatus(workout, selectedDate).status,
        source: WorkoutSource.FromActivity,
        activityId: id,
        workoutRate: workoutRate || null,
        feedbackId,
      }));
  }

  /**
   * TRE-1826 — rozpiska z planu innego niż aktywny dla wybranego dnia. Zwraca pustą listę,
   * gdy dzień wypada poza zakresem planu albo harmonogram nie przewiduje na niego treningu.
   */
  static getPreviewWorkoutsForDay(
    previewPrograms: PreviewProgram[],
    selectedDate: dayjs.Dayjs,
  ): ProgramWorkoutWithMetadata[] {
    const workouts: ProgramWorkoutWithMetadata[] = [];

    for (const { program, details } of previewPrograms) {
      if (!program.workoutsSchedule) {
        continue;
      }

      const programStart = dayjs(program.startDate, "YYYY-MM-DD").startOf("day");
      const programEnd = dayjs(program.endDate, "YYYY-MM-DD").endOf("day");

      if (!selectedDate.isSameOrAfter(programStart) || !selectedDate.isSameOrBefore(programEnd)) {
        continue;
      }

      const isoWeekString = `${selectedDate.get("year")}-${selectedDate.isoWeek()}`;
      const dayOfWeek = weekdays[selectedDate.day()];
      const exceptionWeekSchedule = program.workoutsSchedule.exceptionWeeks?.[isoWeekString] || null;

      const workoutIdForDay = dayOfWeek
        ? exceptionWeekSchedule
          ? exceptionWeekSchedule[dayOfWeek]?.workoutDay
          : program.workoutsSchedule.schedule[
              this.findCurrentWeekScheduleIndex(program.workoutsSchedule.schedule, program.startDate, selectedDate)
            ]?.[dayOfWeek]?.workoutDay
        : null;

      const workoutsById = keyBy(details?.workouts ?? [], "id");
      const archivedWorkoutsById = keyBy(details?.archivedWorkouts ?? [], "id");
      const workout = workoutIdForDay ? (workoutsById[workoutIdForDay] ?? archivedWorkoutsById[workoutIdForDay]) : null;

      if (workout) {
        workouts.push({
          ...workout,
          status: this.getWorkoutStatus(workout, selectedDate, true).status,
          source: exceptionWeekSchedule ? WorkoutSource.FromException : WorkoutSource.FromSchedule,
          readOnly: true,
          programId: program.id,
          programStatus: program.status,
        });
      }
    }

    return workouts;
  }

  static getWorkoutsForDay({
    selectedDate,
    activitiesForSelectedDate,
    clientProgram,
    showOnlyForCurrentProgram,
    programDetails,
    previewPrograms = [],
  }: GetWorkoutsForDayParams): ProgramWorkoutWithMetadata[] {
    if (!clientProgram) {
      // Bez aktywnego planu dziennik pokazywał wyłącznie zapisane aktywności. Plan
      // archiwalny albo zaplanowany nadal ma być widoczny — tylko do odczytu.
      const savedWorkouts = this.getSavedWorkoutsForDay(activitiesForSelectedDate, selectedDate);

      return savedWorkouts.length ? savedWorkouts : this.getPreviewWorkoutsForDay(previewPrograms, selectedDate);
    }
    const mProgramStartDate = dayjs(clientProgram.startDate, "YYYY-MM-DD").startOf("day");
    const mProgramEndDate = dayjs(clientProgram.endDate, "YYYY-MM-DD").endOf("day");
    // case for date outside the current program
    const workouts: ProgramWorkoutWithMetadata[] = [];
    if (!selectedDate.isSameOrAfter(mProgramStartDate) || !selectedDate.isSameOrBefore(mProgramEndDate)) {
      const savedWorkouts = this.getSavedWorkoutsForDay(activitiesForSelectedDate, selectedDate);

      // Zapisana aktywność wygrywa z rozpiską: to, co klientka faktycznie zrobiła, jest
      // prawdziwsze niż plan, z którego dzień pochodzi — i pozostaje w pełni interaktywne.
      return savedWorkouts.length ? savedWorkouts : this.getPreviewWorkoutsForDay(previewPrograms, selectedDate);
    }
    let shouldSkipScheduledWorkouts = false;

    if (
      activitiesForSelectedDate &&
      !isEmpty(activitiesForSelectedDate) &&
      Object.values(activitiesForSelectedDate).some((event) => event.eventDate === selectedDate.format("YYYY-MM-DD"))
    ) {
      Object.values(activitiesForSelectedDate)
        .filter(
          (event) => event.type === ActivityEventType.Workout && event.eventDate === selectedDate.format("YYYY-MM-DD"),
        )
        .forEach(({ data: workout, id, programId, workoutRate, feedbackId }) => {
          if (workout.replicatedFromSchedule && programId === clientProgram.id) {
            shouldSkipScheduledWorkouts = true;
          }
          if (!showOnlyForCurrentProgram || programId === clientProgram.id) {
            workouts.push({
              ...workout,
              status: this.getWorkoutStatus(workout, selectedDate).status,
              source: WorkoutSource.FromActivity,
              activityId: id,
              workoutRate: workoutRate || null,
              feedbackId,
            });
          }
        });
    }
    if (shouldSkipScheduledWorkouts) {
      return workouts;
    }
    const isoWeekOfSelectedDate = selectedDate.isoWeek();
    const dateYear = dayjs(selectedDate).get("year");
    const isoWeekString = `${dateYear}-${isoWeekOfSelectedDate}`;

    const exceptionWeekSchedule = clientProgram.workoutsSchedule?.exceptionWeeks?.[isoWeekString] || null;
    const dayOfWeek = weekdays[selectedDate.day()];

    if (!programDetails) {
      return workouts;
    }

    const workoutsById = keyBy(programDetails.workouts, "id");
    const archivedWorkoutsById = keyBy(programDetails.archivedWorkouts, "id") ?? {};

    if (exceptionWeekSchedule) {
      const workoutIdForDay = dayOfWeek ? exceptionWeekSchedule[dayOfWeek]?.workoutDay : null;
      const regularWorkout = workoutIdForDay ? workoutsById[workoutIdForDay] : null;
      if (workoutIdForDay && regularWorkout) {
        workouts.push({
          ...regularWorkout,
          status: this.getWorkoutStatus(regularWorkout, selectedDate).status,
          source: WorkoutSource.FromException,
        });
      }
      const archivedWorkout = workoutIdForDay ? archivedWorkoutsById?.[workoutIdForDay] : null;
      if (workoutIdForDay && archivedWorkout) {
        workouts.push({
          ...archivedWorkout,
          status: this.getWorkoutStatus(archivedWorkout, selectedDate).status,
          source: WorkoutSource.FromException,
        });
      }
    } else {
      const currentWeekIndex = this.findCurrentWeekScheduleIndex(
        clientProgram?.workoutsSchedule?.schedule ?? null,
        clientProgram.startDate,
        selectedDate,
      );

      const workoutIdForDay = dayOfWeek
        ? clientProgram.workoutsSchedule?.schedule[currentWeekIndex]?.[dayOfWeek]?.workoutDay
        : null;
      const scheduledWorkout =
        workoutIdForDay && (workoutsById[workoutIdForDay] ?? archivedWorkoutsById[workoutIdForDay]);
      if (workoutIdForDay && scheduledWorkout) {
        workouts.push({
          ...scheduledWorkout,
          status: this.getWorkoutStatus(scheduledWorkout, selectedDate).status,
          source: WorkoutSource.FromSchedule,
        });
      }
    }
    return workouts;
  }
}
